/**
 * Tab-side feed-filter needs registry (consumer feed filters, Stage C).
 *
 * Views (and the account plane) declare what they need from the markets
 * feed beyond the game-centric defaults — the normally-excluded classes /
 * market types they render, and (Phase 2 only) the sports they render. The
 * registry unions across every source in this tab and hands the result to
 * the fleet (useHydra's sink), which forwards each feed's share to its
 * transport: the SharedWorker unions again across tabs and applies the
 * flicker hold; the direct-WS client does the same locally. Exactly the
 * `setOrderbookNeeds` → `_routeOrderbookNeeds` shape, one layer over.
 *
 * Outside Zustand on purpose (like `_viewOrderbookNeeds`): a need change must
 * never re-render anything — it only changes what the wire carries.
 *
 * No hold and no debounce at THIS layer: the transport layers own the
 * flicker hold (worker per-port aggregator / direct-client aggregator —
 * route transactions, 2026-10-02), and adding a second one here would
 * double the time a dropped opt-in keeps flowing. Publishes are therefore
 * SYNCHRONOUS — every intermediate union a
 * caller creates reaches the sink.
 *
 * That makes one caller obligation load-bearing: a source that is changing
 * its declaration must re-declare over the top, never clear itself first.
 * `setNeeds` replaces a source's entry, so the clear buys nothing and
 * publishes a union with that source missing. `useHydra`'s floor-cadence
 * negotiation runs off this sink straight to the wire — bypassing the
 * transports' debounce — so that transient is a complete markets filter sent
 * to hydracore, answered with a full replacement snapshot. It wedged hc1 on
 * 2026-09-05 at ~8 flips/minute/tab. See `useFeedNeeds`, which owes this
 * contract its split effects.
 */
import {
  FeedFilterAggregator,
  type AggregatedNeeds,
  type FeedNeeds,
} from '../workers/feedFilter';
import { normalizeSport, wireCodesForSport } from './marketFrameNormalization';
import { aliasSiblings } from '../views/ValueDashboard/types';

export type FeedNeedsSink = (needs: AggregatedNeeds) => void;

let _sink: FeedNeedsSink | null = null;
let _last: AggregatedNeeds | null = null;

const _aggregator = new FeedFilterAggregator<string>(
  (needs) => {
    _last = needs;
    _sink?.(needs);
  },
  // No hold here: the tab publishes every change at once; the transports
  // (worker / direct client) own the flicker hold and the route transaction.
  { holdMs: 0, emitDebounceMs: 0 },
);

/** #1124: one mounted source's declaration, summarized for the Data pressure
 *  card. `'all'` / null mean broad on that axis. */
export interface FeedNeedsSummary {
  sourceId: string;
  sports: 'all' | readonly string[];
  /** The source's game keys, so a group can union them; null = every game. */
  games: readonly string[] | null;
  /** The source's tickers; null = ticker-broad. */
  tickers: readonly string[] | null;
}

// Beside the aggregator, whose per-source map is private to the worker-shared
// class. One entry per mounted declaring source; a `null` declaration (the
// source unmounting) deletes it, so this is bounded by what is mounted.
const _summaries = new Map<string, FeedNeedsSummary>();

function keysOf(axis: Iterable<string> | 'all' | undefined): readonly string[] | null {
  if (axis == null || axis === 'all') return null;
  return [...new Set(axis)];
}

function summarize(sourceId: string, needs: FeedNeeds): FeedNeedsSummary {
  let sports: FeedNeedsSummary['sports'] = 'all';
  if (needs.sports != null && needs.sports !== 'all') {
    // A split sport's ALT key is the same sport to a trader.
    const names = new Set<string>();
    for (const s of needs.sports) names.add(s.toUpperCase().replace(/ALT$/, ''));
    sports = [...names].sort();
  }
  return { sourceId, sports, games: keysOf(needs.games), tickers: keysOf(needs.tickers) };
}

/** Declare (or clear, with `null`) one source's needs. Idempotent per key. */
export function declareFeedNeeds(sourceId: string, needs: FeedNeeds | null): void {
  // Published first: the summary is for the card, the wire filter is load-bearing.
  _aggregator.setNeeds(sourceId, needs);
  if (needs == null) _summaries.delete(sourceId);
  else _summaries.set(sourceId, summarize(sourceId, needs));
}

/** #1124: every mounted source's summary, in declaration order. */
export function feedNeedsBySource(): FeedNeedsSummary[] {
  return [..._summaries.values()];
}

/** Install the fleet publisher. Called by useHydra when the fleet mounts; the
 *  current union is published immediately so needs declared before the
 *  sockets existed are not lost. `null` detaches (fleet teardown). */
export function setFeedNeedsSink(sink: FeedNeedsSink | null): void {
  _sink = sink;
  if (sink) sink(_last ?? _aggregator.current());
}

/** The current union (diagnostics + tests). */
export function currentFeedNeeds(): AggregatedNeeds {
  return _aggregator.current();
}

/** Convert the union back into a per-transport declaration. A viewed sport
 *  is widened to its flow-label siblings' wire codes (NFL → NFL, NFLPROPS,
 *  NFLTOTALS): the parent tab renders every slice, and each slice's edge only
 *  answers for its own label, so the Phase-2 allow-list must name them all. */
export function toFeedNeeds(needs: AggregatedNeeds): FeedNeeds {
  return {
    // Tickers: null = broad = ABSENT on the declaration (`ticker:*`); a set,
    // even empty, crosses as the list so the transport's union stays scoped.
    tickers: needs.tickers == null ? undefined : [...needs.tickers],
    classes: [...needs.classes],
    marketTypes: [...needs.marketTypes],
    // Lane: a viewed sport widens to its flow-label siblings (NFL → NFLPROPS…).
    sports: needs.sports == null ? 'all' : expandSportSiblings(needs.sports),
    // The names survive a broad union (D5.4 — the ALT-only mute reads them).
    // Always published (the boundary rule: every AggregatedNeeds axis
    // crosses); on a narrow union it simply repeats the declared sports.
    namedSports: [...(needs.namedSports ?? [])],
    // Danielle R17 DNL-015: this is the PUBLISHER boundary — every declared
    // axis must cross it or the wire silently keeps the wider scope. Dropping
    // `games` here meant the declarations were correct, the filter builder was
    // correct, and production still went out sport-only. Any axis added to
    // AggregatedNeeds must be added here in the same commit.
    games: needs.games == null ? 'all' : [...needs.games],
    // Tri-state verbatim (g1xl-v2#1053 Part 3): the worker re-aggregates
    // across tabs, and a tab of ticker-scoped sources ('any') must not read
    // as "the whole ladder".
    lineScope: needs.lineScope ?? 'any',
    // Cadence (Part 2d): the tab's fastest opinion, or null = none — the
    // worker takes the fastest across tabs. Floor sports (Part 2e) verbatim.
    cadenceMs: needs.cadenceMs ?? null,
    floorSports: [...(needs.floorSports ?? [])],
    // A provisional source (a board still fetching its allow-list) crosses
    // to the worker so ITS hold keeps the previous scope on the wire.
    provisional: needs.provisional === true,
    // Opt-ins keep their sport attribution across the boundary (per-edge
    // scope): the worker must not re-attribute a GameDetail's NFL classes
    // to every sport this tab views.
    classesBySport: bySport(needs.classesBySport),
    marketTypesBySport: bySport(needs.marketTypesBySport),
    tickersBroadFor: needs.tickers == null ? [...(needs.tickersBroadFor ?? ['*'])].sort() : undefined,
    gamesBroadFor: needs.games == null ? [...(needs.gamesBroadFor ?? ['*'])].sort() : undefined,
    // The named rows / events beside a broad axis, so an edge no source is
    // broad FOR still gets its allow-list (per-edge scope).
    namedTickers: needs.tickers == null ? [...(needs.namedTickers ?? [])].sort() : undefined,
    namedGames: needs.games == null ? [...(needs.namedGames ?? [])].sort() : undefined,
    // ALT events (wedge issue 1): always published — the worker must not
    // re-derive them from a union whose games axis a broad sibling erased.
    altGames: [...(needs.altGames ?? [])].sort(),
    // OddsPapi facets (odds-gamestate-feed-filters D6). The union of the
    // board facets crosses as ONE board — the cross product the union
    // already is — so re-aggregating in the worker yields the same scope.
    odds: toOddsNeeds(needs.odds),
  };
}

function toOddsNeeds(odds: AggregatedNeeds['odds']): FeedNeeds['odds'] {
  // Nothing drawn crosses as absent — the same meaning ("no OP") — so a tab
  // with no OP reader publishes exactly what it published before.
  if (odds == null || (!odds.board && odds.detailGames.size === 0)) return undefined;
  return {
    board: odds.board
      ? { sports: odds.boardSports == null ? 'all' : [...odds.boardSports].sort(), marketTypes: [...odds.marketTypes].sort(), periods: [...odds.periods].sort() }
      : undefined,
    detailGames: [...odds.detailGames].sort(),
  };
}

/** Wire codes → wire codes, each viewed sport unioned with its alias
 *  siblings' codes. Unrecognized codes pass through untouched. */
export function expandSportSiblings(codes: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const code of codes) {
    out.add(code);
    const sport = normalizeSport(code);
    if (!sport) continue;
    for (const sib of aliasSiblings(sport)) for (const c of wireCodesForSport(sib)) out.add(c);
  }
  return [...out].sort();
}

function bySport(map: ReadonlyMap<string, ReadonlySet<string>> | undefined): Record<string, string[]> | undefined {
  if (map == null || map.size === 0) return undefined;
  const out: Record<string, string[]> = {};
  for (const [sport, values] of map) out[sport] = [...values].sort();
  return out;
}
